import { Link } from "react-router-dom";
import { ArrowRight, Compass, Layers, Users, Sparkles } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

const HERO_IMG = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80";

export default function About() {
  return (
    <div className="min-h-screen bg-background grain" data-testid="about-page">
      <SiteHeader />

      {/* ---------- HERO ---------- */}
      <section className="relative border-b border-zinc-200 bg-white overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-y-0 right-0 w-full lg:w-[52%] pointer-events-none"
          data-testid="about-hero-backdrop"
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${HERO_IMG})`,
              backgroundSize: "cover",
              backgroundPosition: "center right",
              filter: "grayscale(1) contrast(1.05)",
            }}
          />
          <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-white via-white/85 to-transparent" />
          <div className="absolute inset-0 bg-white/35" />
        </div>

        <div className="relative mx-auto max-w-7xl px-6 py-28 lg:py-36 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 relative z-10">
            <div className="text-overline text-zinc-500">About EH Inc</div>
            <h1
              className="mt-4 font-serif text-5xl lg:text-7xl font-medium tracking-tight leading-[1.05] text-zinc-900"
              data-testid="about-hero-headline"
            >
              Modern legal guidance.
              <br />
              <span className="italic font-light text-zinc-700">Built around you.</span>
            </h1>
            <p
              className="mt-7 text-lg lg:text-xl text-zinc-600 leading-relaxed max-w-2xl"
              data-testid="about-hero-subheading"
            >
              We combine experienced legal expertise with a purpose-built platform that
              simplifies and improves how legal work is done — giving clients clarity,
              control, and confidence at every step.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/intake"
                className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 transition"
                data-testid="about-cta-start"
              >
                Start a matter <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/#contact"
                className="text-sm text-zinc-700 hover:text-zinc-900 underline-offset-4 hover:underline"
                data-testid="about-cta-contact"
              >
                Speak with us first
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- ABOUT THE FIRM ---------- */}
      <SectionWide
        eyebrow="The firm"
        title="Practical expertise, built on long-term relationships."
        testid="about-firm"
      >
        <p>
          EH&nbsp;Inc is a specialised legal practice focused on property law, estate
          planning, trust structuring, and related legal services.
        </p>
        <p>
          With over <strong className="text-zinc-900">18&nbsp;years of experience</strong>{" "}
          and a strong foundation in conveyancing and fiduciary services, we deliver
          practical, high-quality legal solutions tailored to each client&rsquo;s needs.
        </p>
        <p>
          Our approach is relationship-driven — we believe in building long-term
          partnerships based on trust, integrity, and consistent professionalism. Every
          matter is handled with care, clarity, and a commitment to doing things
          properly.
        </p>
      </SectionWide>

      {/* ---------- A BETTER WAY TO WORK ---------- */}
      <section
        className="bg-zinc-900 text-white border-y border-zinc-900"
        data-testid="about-platform"
      >
        <div className="mx-auto max-w-7xl px-6 py-24 lg:py-32 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-overline text-zinc-400">A better way to work</div>
            <h2 className="mt-4 font-serif text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
              Legal work, <br /> rethought from the client up.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <div className="text-zinc-300 leading-relaxed text-lg space-y-5">
              <p>
                We have built a modern legal platform that rethinks how legal services
                are delivered.
              </p>
              <p>
                Instead of complex, fragmented processes, our system guides clients
                step-by-step, capturing the right information upfront, reducing delays,
                and ensuring nothing is missed.
              </p>
              <p>
                Clients actively participate in the process, while our team focuses on
                what matters most —{" "}
                <strong className="text-white">
                  expert legal advice, oversight, and execution.
                </strong>
              </p>
            </div>

            <div className="mt-12 grid sm:grid-cols-3 gap-6">
              <Pillar
                icon={Compass}
                title="Guided"
                copy="Clear, step-by-step intake — no jargon, no guesswork."
              />
              <Pillar
                icon={Layers}
                title="Connected"
                copy="One workspace for documents, progress, and updates."
              />
              <Pillar
                icon={Sparkles}
                title="Considered"
                copy="Every output is reviewed and approved by a qualified lawyer."
              />
            </div>
          </div>
        </div>
      </section>

      {/* ---------- OUR ECOSYSTEM ---------- */}
      <section className="bg-white border-b border-zinc-200" data-testid="about-ecosystem">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:py-32 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-overline text-zinc-500">Our ecosystem</div>
            <h2 className="mt-4 font-serif text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-zinc-900">
              Bigger than one firm.
            </h2>
            <div className="mt-6 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-zinc-900 text-white flex items-center justify-center">
                <Users className="h-5 w-5" />
              </div>
              <span className="text-sm text-zinc-500">Vetted legal network</span>
            </div>
          </div>
          <div className="lg:col-span-7 text-zinc-700 leading-relaxed text-lg space-y-5">
            <p>Our platform is not limited to a single firm.</p>
            <p>
              We operate within a trusted ecosystem of vetted legal professionals and
              partners, allowing us to match client needs with the right expertise when
              required.
            </p>
            <p>
              This ensures that every client benefits from{" "}
              <strong className="text-zinc-900">
                both personalised service and access to a broader network of trusted
                legal support.
              </strong>
            </p>
          </div>
        </div>
      </section>

      {/* ---------- VALUE PROMISE ---------- */}
      <section className="bg-zinc-50 border-b border-zinc-200" data-testid="about-promise">
        <div className="mx-auto max-w-4xl px-6 py-24 lg:py-32 text-center">
          <div className="text-overline text-zinc-500">Our promise</div>
          <p className="mt-6 font-serif text-2xl lg:text-3xl leading-snug text-zinc-900">
            Our goal is simple:
          </p>
          <p className="mt-6 font-serif text-3xl lg:text-4xl leading-snug text-zinc-900 max-w-3xl mx-auto">
            To remove unnecessary complexity, reduce administrative burden, and give
            clients peace of mind — while ensuring every legal matter is handled with{" "}
            <span className="italic">precision</span> and{" "}
            <span className="italic">care</span>.
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/intake"
              className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 transition"
              data-testid="about-promise-cta"
            >
              Get started <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/calculators"
              className="text-sm text-zinc-700 hover:text-zinc-900 underline-offset-4 hover:underline"
            >
              Try the cost calculator
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Reusable bits                                                      */
/* ------------------------------------------------------------------ */

function SectionWide({ eyebrow, title, children, testid }) {
  return (
    <section className="bg-white border-b border-zinc-200" data-testid={testid}>
      <div className="mx-auto max-w-7xl px-6 py-24 lg:py-32 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-overline text-zinc-500">{eyebrow}</div>
          <h2 className="mt-4 font-serif text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-zinc-900">
            {title}
          </h2>
        </div>
        <div className="lg:col-span-7 text-zinc-700 leading-relaxed text-lg space-y-5">
          {children}
        </div>
      </div>
    </section>
  );
}

function Pillar({ icon: Icon, title, copy }) {
  return (
    <div>
      <div className="h-9 w-9 rounded-full border border-zinc-700 flex items-center justify-center mb-3">
        <Icon className="h-4 w-4 text-zinc-200" />
      </div>
      <div className="font-serif text-lg text-white">{title}</div>
      <div className="text-sm text-zinc-400 leading-relaxed mt-1">{copy}</div>
    </div>
  );
}
